/* Created by Interactive 63 - https://interactive63.com */
/**
 * Interpolation between two property values, shared by track sampling and repeat expansion.
 */
import { interpolateColour } from '../colour';
import { evaluateEasing } from '../easing/evaluate';
import type { DrawValue, Keyframe, PropertyValue, Vec2 } from '../model/types';
import { isDrawValue, isVec2 } from '../model/types';
import { curvePosition, isCurvedSegment } from './motion-curve';
import type { PropertyValueType } from '../properties/registry';

/** Linear interpolation, allowing p outside 0..1 for overshooting easings. */
function lerp(a: number, b: number, p: number): number {
  return a + (b - a) * p;
}

/**
 * Interpolates two values of the same value type at eased progress p, which may fall outside
 * 0..1 for overshooting easings. Mismatched values fall back to a discrete switch at halfway.
 */
export function interpolateValue(
  valueType: PropertyValueType,
  a: PropertyValue,
  b: PropertyValue,
  p: number,
): PropertyValue {
  const discrete = p < 0.5 ? a : b;
  switch (valueType) {
    case 'number':
      return typeof a === 'number' && typeof b === 'number' ? lerp(a, b, p) : discrete;
    case 'colour':
      return typeof a === 'string' && typeof b === 'string' ? interpolateColour(a, b, p) : discrete;
    case 'vec2':
      return isVec2(a) && isVec2(b)
        ? ({ x: lerp(a.x, b.x, p), y: lerp(a.y, b.y, p) } satisfies Vec2)
        : discrete;
    case 'draw':
      return isDrawValue(a) && isDrawValue(b)
        ? ({ start: lerp(a.start, b.start, p), end: lerp(a.end, b.end, p) } satisfies DrawValue)
        : discrete;
    case 'morph':
      // Real path interpolation belongs to the morph module; here the shape simply switches.
      return discrete;
    case 'text':
      // Words cannot blend, so they hold until the next keyframe, as the export does.
      return p < 1 ? a : b;
  }
}

/**
 * Samples a sorted keyframe list at a time. Before the first keyframe and after the last the
 * value holds. Returns undefined for an empty list.
 */
export function sampleKeyframeList(
  valueType: PropertyValueType,
  keyframes: readonly Keyframe[],
  time: number,
): PropertyValue | undefined {
  const first = keyframes[0];
  const last = keyframes[keyframes.length - 1];
  if (!first || !last) return undefined;
  if (time <= first.time) return first.value;
  if (time >= last.time) return last.value;

  const index = keyframes.findLastIndex((kf) => kf.time <= time);
  const from = keyframes[index];
  const to = keyframes[index + 1];
  if (!from || !to) return last.value;

  const progress = (time - from.time) / (to.time - from.time);
  const eased = evaluateEasing(from.easing, progress);
  if (valueType === 'vec2' && isCurvedSegment(from, to)) return curvePosition(from, to, eased);
  return interpolateValue(valueType, from.value, to.value, eased);
}
